'use client';

/**
 * 议程独立页面视图：Excel 表格风
 *
 * - 段标题独占一行（灰底加粗，跨列）
 * - 主题项一行（编号 / 标题 / 时长 / 主讲 / 分类 / 附件状态）
 * - 点击主题行展开看描述 + 附件 + 待办
 * - manager / assignee 才显示上传按钮
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Edit,
  FileText,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/lib/auth';
import { agendaApi, type AgendaTree, type MeetingAgendaItem } from '@/services/api/agenda';
import {
  attachmentApi,
  type MeetingAttachment,
} from '@/services/api/attachment';
import { UploadDialog } from '@/components/file-upload';
import {
  CATEGORY_TAG_CLASS,
  getCategoryTagLabel,
} from './category-tag';
import { ApiClientError } from '@/lib/api-client';

interface Props {
  meetingId: string;
  canManage: boolean;
}

export function AgendaReadonlyView({ meetingId, canManage }: Props) {
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const ta = t.meetingAttendance.attachment;
  const { user } = useAuth();
  const currentUserId = user?.id;

  const [tree, setTree] = useState<AgendaTree | null>(null);
  const [meetingAttachments, setMeetingAttachments] = useState<MeetingAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [meetingUploadOpen, setMeetingUploadOpen] = useState(false);
  const [itemUploadFor, setItemUploadFor] = useState<MeetingAgendaItem | null>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const [agenda, meetingFiles] = await Promise.all([
        agendaApi.getMeetingAgenda(meetingId),
        attachmentApi.listMeetingAttachments(meetingId),
      ]);
      setTree(agenda);
      setMeetingAttachments(meetingFiles.items || []);
    } catch (err) {
      console.error('Failed to load agenda', err);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const toggleItem = useCallback((id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDownloadItemAttachment = useCallback(
    async (attachmentId: string, filename: string) => {
      try {
        await attachmentApi.downloadAgendaItemAttachment(attachmentId, filename);
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : undefined;
        toast.error(code === 'ATTACHMENT_NOT_FOUND' ? ta.notFound : ta.netError);
      }
    },
    [ta.notFound, ta.netError],
  );

  const handleDownloadMeetingAttachment = useCallback(
    async (attachmentId: string, filename: string) => {
      try {
        await attachmentApi.downloadMeetingAttachment(attachmentId, filename);
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : undefined;
        toast.error(code === 'ATTACHMENT_NOT_FOUND' ? ta.notFound : ta.netError);
      }
    },
    [ta.notFound, ta.netError],
  );

  const handleDeleteItemAttachment = useCallback(
    async (itemId: string, attachmentId: string) => {
      if (!window.confirm(ta.deleteConfirm)) return;
      try {
        await attachmentApi.deleteAgendaItemAttachment(itemId, attachmentId);
        toast.success(ta.deleteSuccess);
        await reload();
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : undefined;
        toast.error(code === 'ATTACHMENT_DELETE_FORBIDDEN' ? ta.deleteForbidden : ta.deleteFailed);
      }
    },
    [reload, ta],
  );

  const handleDeleteMeetingAttachment = useCallback(
    async (attachmentId: string) => {
      if (!window.confirm(ta.deleteConfirm)) return;
      try {
        await attachmentApi.deleteMeetingAttachment(meetingId, attachmentId);
        toast.success(ta.deleteSuccess);
        await reload();
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : undefined;
        toast.error(code === 'ATTACHMENT_DELETE_FORBIDDEN' ? ta.deleteForbidden : ta.deleteFailed);
      }
    },
    [meetingId, reload, ta],
  );

  const itemUploader = useMemo(() => {
    return (item: MeetingAgendaItem) => async (
      file: File,
      opts: { onProgress: (p: number) => void; signal: AbortSignal },
    ) => {
      await attachmentApi.uploadAgendaItemAttachment(item.id, file, {
        signal: opts.signal,
        onProgress: (e) => opts.onProgress(e.percent),
      });
    };
  }, []);

  const meetingUploader = useCallback(
    async (
      file: File,
      opts: { onProgress: (p: number) => void; signal: AbortSignal },
    ) => {
      await attachmentApi.uploadMeetingAttachment(meetingId, file, {
        signal: opts.signal,
        onProgress: (e) => opts.onProgress(e.percent),
      });
    },
    [meetingId],
  );

  const hasAnyAgenda = tree && tree.sections.length > 0;

  return (
    <div className="space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex items-center justify-end gap-2">
        {canManage && (
          <>
            <Link href={`/meetingattendance/${meetingId}/agenda/edit`}>
              <Button variant="outline" size="sm">
                <Edit className="mr-2 h-4 w-4" />
                {tag.detailHeaderEditAgenda}
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => setMeetingUploadOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              {tag.detailHeaderUploadMeetingFile}
            </Button>
          </>
        )}
      </div>

      {/* Excel 表格 */}
      {loading ? (
        <div className="text-sm text-gray-500">{t.meetingAttendance.common.loading}</div>
      ) : !hasAnyAgenda ? (
        <div className="rounded border-2 border-dashed border-gray-200 p-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-300" />
          <div className="mt-3 text-sm text-gray-600">{tag.detailEmptyAgenda}</div>
          {canManage && (
            <Link href={`/meetingattendance/${meetingId}/agenda/edit`}>
              <Button variant="outline" size="sm" className="mt-3">
                <Plus className="mr-2 h-4 w-4" />
                {tag.detailEmptyAgendaCta}
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-100">
              <tr className="border-b-2 border-gray-300">
                <th className="w-16 border-r border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">
                  {tag.table.no}
                </th>
                <th className="border-r border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                  {tag.table.topic}
                </th>
                <th className="w-20 border-r border-gray-300 px-3 py-2 text-center font-semibold text-gray-700">
                  {tag.table.time}
                </th>
                <th className="w-36 border-r border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                  {tag.table.presenter}
                </th>
                <th className="w-36 border-r border-gray-300 px-3 py-2 text-left font-semibold text-gray-700">
                  {tag.table.category}
                </th>
                <th className="w-32 px-3 py-2 text-center font-semibold text-gray-700">
                  {tag.table.files}
                </th>
              </tr>
            </thead>
            <tbody>
              {tree!.sections.map((section, sIdx) => (
                <SectionRows
                  key={section.id}
                  section={section}
                  sIdx={sIdx}
                  expandedItems={expandedItems}
                  toggleItem={toggleItem}
                  currentUserId={currentUserId}
                  canManage={canManage}
                  onUploadClick={(item) => setItemUploadFor(item)}
                  onDownload={handleDownloadItemAttachment}
                  onDelete={handleDeleteItemAttachment}
                  t={t}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 会议级资料 */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">
          {ta.sectionTitle}（{ta.categoryAll}）
        </h3>
        {meetingAttachments.length === 0 ? (
          <div className="text-xs text-gray-500">{ta.listEmpty}</div>
        ) : (
          <ul className="space-y-1">
            {meetingAttachments.map((att) => (
              <li
                key={att.id}
                className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs"
              >
                <FileText className="h-4 w-4 text-gray-400" />
                <button
                  type="button"
                  className="flex-1 truncate text-left text-blue-600 hover:underline"
                  onClick={() => handleDownloadMeetingAttachment(att.id, att.filename)}
                >
                  {att.filename}
                </button>
                <span className="text-gray-400">{formatSize(att.size)}</span>
                <span className="text-gray-500">{att.uploadedBy?.displayName}</span>
                {(att.uploadedById === currentUserId || canManage) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteMeetingAttachment(att.id)}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 会议级上传弹窗 */}
      <UploadDialog
        open={meetingUploadOpen}
        onOpenChange={(o) => {
          setMeetingUploadOpen(o);
          if (!o) reload();
        }}
        uploader={meetingUploader}
      />

      {/* 议程项上传弹窗 */}
      <UploadDialog
        open={!!itemUploadFor}
        onOpenChange={(o) => {
          if (!o) {
            setItemUploadFor(null);
            reload();
          }
        }}
        uploader={
          itemUploadFor
            ? itemUploader(itemUploadFor)
            : async () => {
                /* noop */
              }
        }
        title={itemUploadFor ? `${ta.upload} - ${itemUploadFor.title}` : undefined}
      />
    </div>
  );
}

// --------------- 段 + 主题项行子组件 ---------------

interface SectionRowsProps {
  section: AgendaTree['sections'][number];
  sIdx: number;
  expandedItems: Set<string>;
  toggleItem: (id: string) => void;
  currentUserId: string | undefined;
  canManage: boolean;
  onUploadClick: (item: MeetingAgendaItem) => void;
  onDownload: (attachmentId: string, filename: string) => void;
  onDelete: (itemId: string, attachmentId: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function SectionRows({
  section,
  sIdx,
  expandedItems,
  toggleItem,
  currentUserId,
  canManage,
  onUploadClick,
  onDownload,
  onDelete,
  t,
}: SectionRowsProps) {
  const tag = t.meetingAttendance.agenda;
  const ta = t.meetingAttendance.attachment;

  return (
    <>
      {/* 段标题行（跨列） */}
      <tr className="bg-gray-50">
        <td
          colSpan={6}
          className="border-b border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-900"
        >
          <span className="text-gray-500">{sIdx + 1}. </span>
          {section.title}
          <span className="ml-2 text-xs font-normal text-gray-500">
            {tag.section.itemsCount.replace('{n}', String(section.items.length))}
          </span>
        </td>
      </tr>

      {/* 主题项行 */}
      {section.items.map((item, iIdx) => {
        const expanded = expandedItems.has(item.id);
        const pendingCount = item.uploadTasks.filter((x) => x.status === 'PENDING').length;
        const uploadedCount = item.uploadTasks.filter((x) => x.status === 'UPLOADED').length;
        const itemAssignedToMe = item.uploadTasks.some(
          (x) => x.assigneeUserId === currentUserId && x.status === 'PENDING',
        );
        const filesCount = item.attachments.length;

        return (
          <>
            <tr
              key={item.id}
              id={`agenda-item-${item.id}`}
              className="cursor-pointer border-b border-gray-200 bg-white hover:bg-blue-50/30"
              onClick={() => toggleItem(item.id)}
            >
              {/* # */}
              <td className="border-r border-gray-200 px-3 py-2 text-center align-top text-sm text-gray-600">
                <div className="flex items-center justify-center gap-1">
                  {expanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                  )}
                  <span>
                    {sIdx + 1}.{iIdx + 1}
                  </span>
                </div>
              </td>
              {/* Topic */}
              <td className="border-r border-gray-200 px-3 py-2 align-top text-sm text-gray-900">
                {item.title}
              </td>
              {/* Time */}
              <td className="border-r border-gray-200 px-3 py-2 text-center align-top text-sm text-gray-700">
                {typeof item.timeMinutes === 'number' && item.timeMinutes > 0
                  ? `${item.timeMinutes} min`
                  : '—'}
              </td>
              {/* Presenter */}
              <td className="border-r border-gray-200 px-3 py-2 align-top text-sm text-gray-700">
                {item.presenter?.displayName || '—'}
              </td>
              {/* Category */}
              <td className="border-r border-gray-200 px-3 py-2 align-top text-sm">
                {item.categoryTag ? (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${CATEGORY_TAG_CLASS[item.categoryTag]}`}
                  >
                    {getCategoryTagLabel(item.categoryTag, t)}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              {/* Files / Tasks */}
              <td className="px-3 py-2 text-center align-top text-xs">
                <div className="flex flex-wrap items-center justify-center gap-1">
                  {filesCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                      <FileText className="h-3 w-3" />
                      {filesCount}
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">
                      {tag.uploadTask.statusBadgePending.replace('{n}', String(pendingCount))}
                    </span>
                  )}
                  {uploadedCount > 0 && (
                    <span className="rounded bg-green-100 px-1.5 py-0.5 text-green-800">
                      {tag.uploadTask.statusBadgeUploaded.replace('{n}', String(uploadedCount))}
                    </span>
                  )}
                  {filesCount === 0 && pendingCount === 0 && uploadedCount === 0 && (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </td>
            </tr>

            {/* 展开行：描述 + 附件 */}
            {expanded && (
              <tr key={`${item.id}-expand`} className="border-b border-gray-200 bg-gray-50/50">
                <td colSpan={6} className="px-6 py-3">
                  <div className="space-y-3">
                    {item.description && (
                      <div className="whitespace-pre-wrap text-sm text-gray-700">
                        {item.description}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-medium text-gray-700">{ta.sectionTitle}</div>
                        {(canManage || itemAssignedToMe) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onUploadClick(item);
                            }}
                          >
                            <Upload className="mr-1 h-3 w-3" />
                            {tag.item.uploadFile}
                          </Button>
                        )}
                      </div>
                      {item.attachments.length === 0 ? (
                        <div className="mt-1 text-xs text-gray-500">{ta.listEmpty}</div>
                      ) : (
                        <ul className="mt-2 space-y-1">
                          {item.attachments.map((att) => (
                            <li
                              key={att.id}
                              className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1 text-xs"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FileText className="h-3 w-3 text-gray-400" />
                              <button
                                type="button"
                                className="flex-1 truncate text-left text-blue-600 hover:underline"
                                onClick={() => onDownload(att.id, att.filename)}
                              >
                                {att.filename}
                              </button>
                              <span className="text-gray-400">{formatSize(att.size)}</span>
                              <span className="text-gray-500">{att.uploadedBy?.displayName}</span>
                              {(att.uploadedById === currentUserId || canManage) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onDelete(item.id, att.id)}
                                >
                                  <Trash2 className="h-3 w-3 text-red-500" />
                                </Button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </>
        );
      })}
    </>
  );
}

function formatSize(sizeStr: string): string {
  const n = Number(sizeStr);
  if (!Number.isFinite(n)) return sizeStr;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
